import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { confirmAlert } from 'react-confirm-alert';
import Modal from '../General/Modal';

import { closeNewCircleModal, openAuthModal } from '../../Actions/UIActions';
import { createCuratorGroupFromUserIds, createCuratorGroupUser, syncHierarchy } from '../../Actions/ShopActions';

import NewCircleModalIntro from './Elements/NewCircleModal/NewCircleModalIntro';
import NewCircleModalCategories from './Elements/NewCircleModal/NewCircleModalCategories';
import NewCircleModalBrands from './Elements/NewCircleModal/NewCircleModalBrands';
import NewCircleModalCurators from './Elements/NewCircleModal/NewCircleModalCurators';
import NewCircleModalFooter from './Elements/NewCircleModal/NewCircleModalFooter';

import NewCircleModalSearch from './Elements/NewCircleModal/NewCircleModalSearch';
import NewCircleModalData from './Elements/NewCircleModal/NewCircleModalData';
import NewCircleModalFinal from './Elements/NewCircleModal/NewCircleModalFinal';

import { getLinkToShopForCuratorGroup } from '../../Helpers/shop_helpers';
import { getNewCircleModalStartingParams } from '../../Helpers/ui_helpers';

import './NewCircleModal.scss';

const NewCircleModal = props => {
  const { ui } = props;
  const params = getNewCircleModalStartingParams(ui);

  // Preloaded
  const activeGroup = params.initial_group;

  // Anything Needed for Data Loading
  const [brandsToConsider, setBrandsToConsider] = React.useState([]);
  const [isLoadingBrands, setIsLoadingBrands] = React.useState(false);

  // Other attributes
  const [circleTitle, setCircleTitle] = React.useState(activeGroup?.title || '');
  const [circleHierarchy, setCircleHierarchy] = React.useState({
    Industry_id: activeGroup?.Industry_id,
    Department_id: activeGroup?.Department_id
  });

  // Configurations
  const [selectedIndustries, setSelectedIndustries] = React.useState([]);
  const [selectedDepartments, setSelectedDepartments] = React.useState([]);
  const [selectedBrands, setSelectedBrands] = React.useState([]);
  const [selectedCurators, setSelectedCurators] = React.useState(params.initial_curators || []); // Minimum format of ({ id, name, image })

  // Other Configurations
  const onlyHasInitialCurators =
    selectedCurators.length > 0 &&
    params.initial_curators &&
    selectedCurators.every(curator => params.initial_curators.some(c => c.id === curator.id));
  const canGoToFinalPanel = selectedCurators.length > 0;
  const canAddSelectedCuratorsToActiveGroup = selectedCurators.length > 0 && activeGroup;
  const MIN_CURATORS_TO_COMPLETE = activeGroup ? 1 : 3;

  // Active Group
  const [isAddingCuratorsToActiveGroup, setIsAddingCuratorsToActiveGroup] = React.useState(false);
  const addCuratorsToActiveGroupAndClose = async () => {
    setIsAddingCuratorsToActiveGroup(true);
    window.__ADD_EVENT__('Shop - New Circle Modal - Add Curators To Active Group', {
      num_curators: selectedCurators.length,
      Group_title: activeGroup?.title,
      Group_id: activeGroup?.id
    });

    try {
      for (const curator of selectedCurators) {
        await props.createCuratorGroupUser({ CuratorGroup_id: activeGroup.id, User_id: curator.id });
      }
      window.ALERT.success(`Added ${selectedCurators.length} curator${selectedCurators.length !== 1 ? 's' : ''} to circle`);
      props.closeNewCircleModal();
    } catch (error) {
      window.ALERT.error(error || 'Unknown error creating circle, please try again.');
    }

    setIsAddingCuratorsToActiveGroup(false);
  };

  const customCompleteActionActiveGroup = {
    label: `Add ${selectedCurators.length} Curator${selectedCurators.length !== 1 ? 's' : ''} To Circle`,
    isLoading: isAddingCuratorsToActiveGroup,
    className: 'primary',
    disabled: !canGoToFinalPanel,
    onClick: addCuratorsToActiveGroupAndClose
  };

  const clickCloseConfirmLosingData = () => {
    if (selectedCurators.length && !onlyHasInitialCurators) {
      confirmAlert({
        title: 'Are you sure?',
        message: 'Are you sure you want to stop creating this circle? You will lose all of your progress.',
        buttons: [
          { label: 'No', className: 'cancel', onClick: () => {} },
          { label: 'Yes', onClick: () => props.closeNewCircleModal() }
        ]
      });
    } else {
      props.closeNewCircleModal();
    }
  };

  // Scroll to top when changing panels
  const bodyRef = React.useRef(null);
  const goToNextPanel = () => {
    setActivePanelIdx(Math.min(panels.length - 1, activePanelIdx + 1));
    bodyRef.current.scrollTo({ top: 0 });

    window.__ADD_EVENT__('Shop - New Circle Modal - Go To Next Panel', {
      panel: panels[activePanelIdx + 1]?.variable
    });
  };
  const goToPreviousPanel = () => {
    setActivePanelIdx(Math.max(0, activePanelIdx - 1));
    bodyRef.current.scrollTo({ top: 0 });

    window.__ADD_EVENT__('Shop - New Circle Modal - Go To Previous Panel', {
      panel: panels[activePanelIdx - 1]?.variable
    });
  };

  const goToFinalPanel = () => {
    setIsOnSearchPanel(false);
    setActivePanelIdx(panels.length - 1);
  };

  const goBackToFirstPanel = () => {
    setIsOnSearchPanel(false);
    setActivePanelIdx(0);
  };

  const backFromFinalPanel = () => {
    const wentThroughDiscover = selectedBrands.length > 0 || selectedDepartments.length > 0 || selectedIndustries.length > 0;
    if (wentThroughDiscover) {
      goToPreviousPanel();
    } else {
      goBackToFirstPanel();
    }
  };

  // Final Panel
  const createCircleAndHeadToTab = async (tab = 'latest') => {
    if (!selectedCurators.length) {
      window.ALERT.error('Please select at least one curator');
      return null;
    }
    if (!circleTitle) {
      window.ALERT.error('Please enter a title for your circle');
      return null;
    }

    try {
      const resp = await props.createCuratorGroupFromUserIds(
        selectedCurators.map(c => c.id),
        _.omitBy(
          {
            title: circleTitle,
            ...circleHierarchy
          },
          _.isNil
        ),
        'New Circle Modal'
      );
      window.ALERT.success('Circle created successfully!');
      props.closeNewCircleModal();
      props.history.push(getLinkToShopForCuratorGroup(resp.group, { tab }));
      return resp.group;
    } catch (error) {
      window.ALERT.error(error || 'Unknown error creating circle, please try again.');
      return null;
    }
  };

  // Searching
  const [isOnSearchPanel, setIsOnSearchPanel] = React.useState(false);
  const pressToSearch = () => {
    setIsOnSearchPanel(true);
    window.__ADD_EVENT__('Shop - New Circle Modal - Press To Search');
  };
  const closeSearch = () => {
    setIsOnSearchPanel(false);
    window.__ADD_EVENT__('Shop - New Circle Modal - Close Search');
  };

  // Props
  const filterProps = {
    selectedCurators,
    selectedIndustries,
    selectedDepartments,
    selectedBrands,
    setSelectedIndustries,
    setSelectedDepartments,
    setSelectedBrands,
    setSelectedCurators
  };

  const loadingProps = {
    isLoadingBrands,
    setIsLoadingBrands
  };

  const groupProps = {
    activeGroup
  };

  // Panels
  const panels = [
    {
      variable: 'intro',
      body: (
        <NewCircleModalIntro
          user={props.user}
          goToNextPanel={goToNextPanel}
          goToFinalPanel={goToFinalPanel}
          pressToSearch={pressToSearch}
          justSignedUp={params.justSignedUp}
          initial_curators={params.initial_curators || []}
          {...filterProps}
          {...groupProps}
        />
      ),
      hideFooter: !selectedCurators.length,
      customSubmit: [
        {
          label: 'Discover More',
          className: 'secondary',
          onClick: goToNextPanel
        },
        ...(canAddSelectedCuratorsToActiveGroup
          ? [customCompleteActionActiveGroup]
          : [
              {
                label: 'Finalize Circle',
                className: 'primary',
                disabled: !selectedCurators.length,
                onClick: goToFinalPanel
              }
            ])
      ]
    },
    {
      variable: 'categories',
      body: <NewCircleModalCategories shop={props.shop} {...filterProps} />,
      canGoNextData: {
        disabled: selectedIndustries.length + selectedDepartments.length < 1,
        tooltip: 'Please select at least at least one option.',
        disabledBtnText: `Select ${1 - (selectedIndustries.length + selectedDepartments.length)} more`
      },
      canSearch: true
    },
    {
      variable: 'brands',
      body: <NewCircleModalBrands brandsToConsider={brandsToConsider} shop={props.shop} {...filterProps} {...loadingProps} />,
      canGoNextData: {
        disabled: selectedBrands.length < 3,
        tooltip: 'Please select at least three options below.',
        disabledBtnText: `Select ${3 - selectedBrands.length} more`
      },
      canSearch: true
    },
    {
      variable: 'curators',
      body: <NewCircleModalCurators shop={props.shop} {...filterProps} {...groupProps} />,
      canGoNextData: {
        disabled: activeGroup ? selectedCurators.length < 1 : selectedCurators.length < MIN_CURATORS_TO_COMPLETE,
        tooltip: 'Please select at least three options below.',
        disabledBtnText: MIN_CURATORS_TO_COMPLETE === 1 ? 'Select a Curator' : `Select ${MIN_CURATORS_TO_COMPLETE - selectedCurators.length} more`
      },
      canSearch: true,
      customSubmit: canAddSelectedCuratorsToActiveGroup ? [customCompleteActionActiveGroup] : null
    },
    {
      variable: 'final',
      body: (
        <NewCircleModalFinal
          {...filterProps}
          {...groupProps}
          user={props.user}
          shop={props.shop}
          onlyHasInitialCurators={onlyHasInitialCurators}
          circleTitle={circleTitle}
          setCircleTitle={setCircleTitle}
          circleHierarchy={circleHierarchy}
          setCircleHierarchy={setCircleHierarchy}
          goToPreviousPanel={goToPreviousPanel}
          goBackToFirstPanel={goBackToFirstPanel}
          createCircleAndHeadToTab={createCircleAndHeadToTab}
          backFromFinalPanel={backFromFinalPanel}
          openAuthModal={props.openAuthModal}
        />
      ),
      hideFooter: true
    }
  ].map((panel, idx) => ({ ...panel, idx }));

  // Current Panel Index
  const defaultFromParams = params.panel ? panels.findIndex(p => p.variable === params.panel) || 0 : 0;
  const [activePanelIdx, setActivePanelIdx] = React.useState(defaultFromParams);

  // Search Panel
  const searchPanel = {
    variable: 'search',
    body: <NewCircleModalSearch {...filterProps} shop={props.shop} closeSearch={closeSearch} {...groupProps} />,
    customSubmit: [
      {
        label: 'Back from Search',
        className: 'secondary',
        onClick: closeSearch
      },
      ...(canAddSelectedCuratorsToActiveGroup
        ? [customCompleteActionActiveGroup]
        : [
            {
              label: canGoToFinalPanel
                ? activeGroup
                  ? `Add ${selectedCurators.length} Curator${selectedCurators.length !== 1 ? 's' : ''} To Circle`
                  : 'Finalize Circle'
                : 'Add Curators',
              isLoading: isAddingCuratorsToActiveGroup,
              className: 'primary',
              disabled: !canGoToFinalPanel,
              onClick: activeGroup ? addCuratorsToActiveGroupAndClose : goToFinalPanel
            }
          ])
    ]
  };

  // Panel Attributes
  const activePanel = isOnSearchPanel ? searchPanel : panels[activePanelIdx];

  return (
    <Modal
      showClose
      noPadding
      visible
      close={clickCloseConfirmLosingData}
      className='new-circle-modal-outer-container'
      innerClassName='new-circle-modal-inner-container'
      contentClassName='new-circle-modal-content'
    >
      <NewCircleModalData
        selectedIndustries={selectedIndustries}
        selectedDepartments={selectedDepartments}
        activePanel={activePanel}
        brandsToConsider={brandsToConsider}
        setBrandsToConsider={setBrandsToConsider}
        syncHierarchy={props.syncHierarchy}
        {...loadingProps}
      />
      <div ref={bodyRef} className='new-circle-modal-body'>
        {isOnSearchPanel ? searchPanel.body : activePanel.body}
      </div>
      {!activePanel.hideFooter && (
        <NewCircleModalFooter
          {...filterProps}
          {...groupProps}
          activePanel={activePanel}
          goToNextPanel={goToNextPanel}
          goToPreviousPanel={goToPreviousPanel}
          setSelectedIndustries={setSelectedIndustries}
          setSelectedDepartments={setSelectedDepartments}
          pressToSearch={pressToSearch}
        />
      )}
    </Modal>
  );
};

NewCircleModal.propTypes = {
  ui: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  shop: PropTypes.object.isRequired,
  closeNewCircleModal: PropTypes.func.isRequired,
  openAuthModal: PropTypes.func.isRequired,
  createCuratorGroupFromUserIds: PropTypes.func.isRequired,
  createCuratorGroupUser: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { shop, user, ui } = state;
  return { shop, user, ui };
};

export default connect(mapStateToProps, {
  closeNewCircleModal,
  createCuratorGroupFromUserIds,
  createCuratorGroupUser,
  openAuthModal,
  syncHierarchy
})(withRouter(NewCircleModal));
